import { negate } from 'lodash';
import * as R from 'remeda';
import equal from 'fast-deep-equal/es6/react';
import { push, replace } from 'redux-first-history';
import {
  ActionsObservable,
  combineEpics,
  StateObservable,
} from 'redux-observable';
import {
  combineLatest,
  concat,
  EMPTY,
  forkJoin,
  from,
  fromEvent,
  merge,
  Observable,
  of,
  throwError,
  timer,
  zip,
} from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import {
  catchError,
  delay,
  distinctUntilChanged,
  expand,
  filter,
  first,
  map,
  mapTo,
  mergeMap,
  pairwise,
  pluck,
  reduce,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { endpoints, getIDFromUrl } from '@rossum/api-client';
import { FailedAutomationResult } from '@rossum/api-client/automationBlockers';
import { Label } from '@rossum/api-client/labels';
import { Page } from '@rossum/api-client/pages';
import { Queue } from '@rossum/api-client/queues';
import { Relation } from '@rossum/api-client/relations';
import { AnnotationStatus } from '@rossum/api-client/shared';
import { User } from '@rossum/api-client/users';
import { Workspace } from '@rossum/api-client/workspaces';
import { absoluteApiUrl, apiUrl, isEmbedded } from '../../../constants/config';
import { FETCH_INTERVAL } from '../../../constants/values';
import { isFeatureEnabled } from '../../../features/feature-enablement/featureEnablement';
import { embeddedFeatureSelector } from '../../../features/pricing/selectors';
import {
  confirmErrorHandler,
  errorHandler,
  notFoundErrorHandler,
  retryExponentialBackoff,
} from '../../../lib/api';
import { api } from '../../../lib/apiClient';
import { createAuthJSONHeaders, report } from '../../../lib/apiHelpers';
import convertKeys, { camelToSnake } from '../../../lib/keyConvertor';
import { assertNever } from '../../../lib/typeUtils';
import {
  constructDocumentUrl,
  getCurrentAnnotationId,
  getEditModeUrl,
  getIDFromString,
  isSplitPathname,
  isValidationPathname,
  parse,
} from '../../../lib/url';
import { getAnnotationBacklink } from '../../../routes/useAnnotationBacklink';
import {
  ProcessingDuration,
  timeSpent,
} from '../../../timers/timeSpent/timeSpent';
import { Annotation } from '../../../types/annotation';
import { Url } from '../../../types/basic';
import { Document } from '../../../types/document';
import { State } from '../../../types/state';
import { RootActionType } from '../../rootActions';
import { popAnnotationFromStack } from '../annotations/actions';
import { fetchBboxes } from '../bboxes/actions';
import {
  addSchemasToDatapoints,
  fetchDatapoints,
  fetchDatapointsFulfilled,
  lastDatapointsForTimeSpentUpdate,
} from '../datapoints/actions';
import { confirmEditModeFulfilled } from '../editMode/actions';
import { DOCUMENTS_QUERY } from '../localStorage/actions';
import { prevalidateAnnotationsEnabledSelector } from '../organizationGroup/selectors';
import { fetchSchema } from '../schema/actions';
import { fetchAnnotationStackFulfilled } from '../stack/actions';
import { leaveValidation, startValidation } from '../ui/actions';
import {
  isUserViewer,
  onConfirmNavigateSelector,
  userLabConfigSelector,
  userRoleNameSelector,
} from '../user/selectors';
import { locationChange, makeEpic } from '../utils';
import {
  annotationExpired,
  cancelAnnotation,
  cancelAnnotationFulfilled,
  confirmAnnotation,
  confirmAnnotationFulfilled,
  deleteAnnotation,
  deleteAnnotationFulfilled,
  displayAnnotation,
  editModeForParentStartedAction,
  fetchAnnotationFulfilled,
  fetchProcessingDuration,
  fetchProcessingDurationFulfilled,
  fetchSuggestedEdit,
  fetchSuggestedEditFulfilled,
  nextAnnotableAnnotation,
  nextAnnotation,
  openOriginalDocumentFromEditMode,
  postponeAnnotation,
  postponeAnnotationFulfilled,
  refetchAnnotation,
  refetchAnnotationEmails,
  refetchAnnotationEmailsFulfilled,
  rejectAnnotation,
  rejectAnnotationFulfilled,
  startAnnotation,
  startAnnotationFulfilled,
} from './actions';
import {
  getMissingAnnotationIndexes,
  getReviewableStatusesFromAnnotationsQuery,
  handleAnnotationStatus,
  readOnlyModeEndingActionCreators,
  validationEndingActionCreators,
} from './helpers';
import { pagesSelector } from './selectors';
import {
  AnnotationLoadMode,
  DeleteAnnotationMeta,
  PostponeAnnotationMeta,
  SuggestedEdit,
} from './types';
import { withProcessingDuration } from './withProcessingDuration';

const statusesWithoutContent: Array<AnnotationStatus> = [
  'importing',
  'failed_import',
];

const annotationDataToFetch = (
  { schema, url, status, restrictedAccess }: Annotation,
  mode: AnnotationLoadMode
) => {
  const fetchSchemaOnFullLoad = [mode === 'full-load' && fetchSchema(schema)];
  const extendedData = [
    fetchDatapoints(url),
    // Bboxes, pages, and schema are not going to change while somebody
    // else is reviewing an annotation. No need to refetch them again
    mode === 'full-load' && fetchBboxes(url),
    ...fetchSchemaOnFullLoad,
    // TODO this is an interesting case read-only annotation switched into reviewing
    mode === 'full-load' && fetchProcessingDuration(url),
  ];

  const showContent =
    restrictedAccess !== true && !statusesWithoutContent.includes(status);

  return R.pipe(
    showContent ? extendedData : fetchSchemaOnFullLoad,
    R.filter(R.isTruthy)
  );
};

const fetchAnnotationDataEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf([startAnnotationFulfilled, displayAnnotation])),
    filter(action => !!action.meta.url),
    switchMap(action => {
      return action$.pipe(
        filter(isActionOf(fetchAnnotationFulfilled)),
        mergeMap(({ payload: { annotation } }) =>
          from(annotationDataToFetch(annotation, action.meta.mode))
        )
      );
    })
  )
);

export const startAnnotationEpic = makeEpic((action$, state$, { authPost$ }) =>
  action$.pipe(
    filter(isActionOf(startAnnotation)),
    pluck('payload'),
    switchMap(({ id, mode }) => {
      const statuses = getReviewableStatusesFromAnnotationsQuery();

      return authPost$<{ annotation: string }>(
        `${apiUrl}/annotations/${id}/start`,
        statuses.length && state$.value.stack.includes(id) ? { statuses } : {}
      ).pipe(
        retryExponentialBackoff(),
        takeUntil(
          action$.pipe(filter(isActionOf(validationEndingActionCreators)))
        ),
        map(({ annotation }) => startAnnotationFulfilled(annotation, mode)),
        catchError(handleAnnotationStatus(id, mode)),
        catchError(errorHandler)
      );
    })
  )
);

const fetchAnnotationEpic = makeEpic((action$, _, { authGetJSON$ }) =>
  action$.pipe(
    filter(
      isActionOf([
        startAnnotationFulfilled,
        displayAnnotation,
        refetchAnnotation,
      ])
    ),
    switchMap(action => {
      const annotationId = getIDFromString(action.meta.url);

      const query = {
        id: [annotationId],
        sideload: [
          'automation_blockers',
          'documents',
          'modifiers',
          'pages',
          'relations',
          'queues',
          'workspaces',
          'exported_bys',
          'confirmed_bys',
          'labels',
        ],
      };

      return authGetJSON$<{
        automationBlockers: FailedAutomationResult[];
        documents: Document[];
        modifiers: User[];
        pages: Page[];
        results: Annotation[];
        relations: Relation[];
        queues: Queue[];
        workspaces: Workspace[];
        confirmedBys: User[];
        exportedBys: User[];
        labels: Label[];
      }>(`${apiUrl}/annotations`, {
        query,
      }).pipe(
        takeUntil(
          action$.pipe(filter(isActionOf([...validationEndingActionCreators])))
        ),
        mergeMap(
          ({
            results,
            automationBlockers,
            documents,
            modifiers,
            pages,
            relations,
            queues,
            workspaces,
            confirmedBys,
            exportedBys,
            labels,
          }) => {
            const annotation = results[0];
            if (!annotation) return notFoundErrorHandler();

            const document = documents[0];
            const workspace = workspaces[0];
            const queue = queues[0];

            return of(
              fetchAnnotationFulfilled(
                annotation,
                annotation.restrictedAccess
                  ? {
                      automationBlockers: [],
                      document,
                      modifier: { user: undefined },
                      pages: [],
                      relations: [],
                      workspace,
                      queue,
                      confirmedBy: undefined,
                      exportedBy: undefined,
                      labels: [],
                    }
                  : {
                      automationBlockers: automationBlockers[0]
                        ? automationBlockers[0].content
                        : [],
                      document,
                      workspace,
                      queue,
                      modifier: annotation.modifier
                        ? {
                            user: modifiers.find(
                              modifier => modifier.url === annotation.modifier
                            ),
                          }
                        : undefined,
                      pages: pages.sort((a, b) => a.number - b.number),
                      relations,
                      confirmedBy: annotation.confirmedBy
                        ? confirmedBys.find(
                            modifier => modifier.url === annotation.confirmedBy
                          )
                        : undefined,
                      exportedBy: annotation.exportedBy
                        ? exportedBys.find(
                            modifier => modifier.url === annotation.exportedBy
                          )
                        : undefined,
                      labels,
                    },
                'editMode' in action.meta ? action.meta.editMode : undefined
              )
            );
          }
        ),
        catchError(errorHandler)
      );
    })
  )
);

const redirectAfterFetchAnnotation = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchAnnotationFulfilled)),
    filter(
      ({
        payload: {
          annotation: { id },
        },
      }) => {
        const {
          router: {
            location: { pathname },
          },
        } = state$.value;
        return getCurrentAnnotationId(pathname) !== id;
      }
    ),
    map(
      ({
        payload: {
          annotation: { id },
          editMode,
        },
      }) => {
        const query = parse(state$.value.router.location.search);

        if (editMode) {
          const isSplitEnabled = isFeatureEnabled(
            'mat-1949-splitting-view',
            userLabConfigSelector(state$.value)
          );

          return replace(getEditModeUrl(isSplitEnabled, { id, query }));
        }

        return replace(constructDocumentUrl({ id, query, nestedPath: '' }));
      }
    )
  )
);

const nextAnnotationEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(nextAnnotation)),
    map(action => action.meta.nSteps),
    // Ignore the other variant
    filter((nSteps): nSteps is number => typeof nSteps === 'number'),
    map((nSteps = 1) => {
      const {
        router: {
          location: { pathname },
        },
        stack,
      } = state$.value;
      const annotationId = getCurrentAnnotationId(pathname);

      return { nSteps, stack, annotationId };
    }),
    map(({ stack, annotationId, nSteps }) => {
      const currentAnnotationIndex = stack.indexOf(annotationId);
      const nextAnnotationId = stack[currentAnnotationIndex + nSteps];
      const defaultListLocation = getAnnotationBacklink();

      return nextAnnotationId
        ? push(
            constructDocumentUrl({
              id: nextAnnotationId,
              query: {
                ...parse(state$.value.router.location.search),
                datapointPath: undefined,
              },
            })
          )
        : replace(defaultListLocation);
    })
  )
);

const nextAnnotableAnnotationEpic = makeEpic((action$, state$, { authPost$ }) =>
  action$.pipe(
    filter(isActionOf(fetchAnnotationFulfilled)),
    switchMap(
      ({
        payload: {
          annotation: { id: annotationId },
        },
      }) =>
        action$.pipe(
          filter(isActionOf(nextAnnotableAnnotation)),
          mergeMap(({ payload: { edit } }) => {
            const { stack } = state$.value;

            const defaultListLocation = getAnnotationBacklink();

            const currentAnnotationIndex = stack.indexOf(annotationId);
            const slicedStack = stack.slice(currentAnnotationIndex + 1);

            if (R.isEmpty(slicedStack)) return of(replace(defaultListLocation));

            const statuses = getReviewableStatusesFromAnnotationsQuery();

            return combineLatest([
              authPost$<{ annotation: Url }>(`${apiUrl}/annotations/next`, {
                annotations: slicedStack.map(
                  id => `${absoluteApiUrl}/annotations/${id}`
                ),
                ...(statuses.length && { statuses }),
              }).pipe(retryExponentialBackoff()),
              action$.pipe(
                filter(isActionOf(leaveValidation)),
                map(() => true),
                startWith(false)
              ),
            ]).pipe(
              map(([{ annotation: annotationUrl }, shouldCancel]) => {
                const documentListQuery = localStorage.getItem(DOCUMENTS_QUERY);
                const documentListUrl = documentListQuery
                  ? `/documents?${documentListQuery}`
                  : '/documents';

                const redirectUrl = documentListUrl;

                if (!annotationUrl) return replace(redirectUrl);
                if (shouldCancel) return cancelAnnotation(annotationUrl);

                const nextId = getIDFromUrl(annotationUrl);

                if (edit) {
                  const isSplitEnabled = isFeatureEnabled(
                    'mat-1949-splitting-view',
                    userLabConfigSelector(state$.value)
                  );

                  return push(getEditModeUrl(isSplitEnabled, { id: nextId }));
                }

                return push(
                  constructDocumentUrl({ id: nextId, nestedPath: '' })
                );
              }),
              catchError((error: AjaxError) => {
                const currentAnnotationIndexInStack =
                  state$.value.stack.indexOf(annotationId);

                // if one of the next annotations have been moved to a queue the user does not have access to
                // we filter them out and continue to the next aannotable annotaiton instead of throwing an error
                const missingAnnotationIndexes =
                  getMissingAnnotationIndexes(error);

                if (
                  missingAnnotationIndexes &&
                  missingAnnotationIndexes?.length > 0 &&
                  currentAnnotationIndexInStack !== -1
                ) {
                  const firstHalf = stack.slice(
                    0,
                    currentAnnotationIndexInStack + 1
                  );
                  const secondHalf = stack.slice(
                    currentAnnotationIndexInStack + 1
                  );

                  const filteredSecondHalf = secondHalf.filter(
                    (_, index) => !missingAnnotationIndexes.includes(index)
                  );
                  const filteredStack = [...firstHalf, ...filteredSecondHalf];

                  return of(
                    fetchAnnotationStackFulfilled(filteredStack),
                    nextAnnotableAnnotation()
                  );
                }
                return errorHandler(error);
              })
            );
          })
        )
    )
  )
);

const resetDatapointPath = makeEpic((action$, state$) =>
  action$.pipe(
    filter(
      isActionOf([
        ...validationEndingActionCreators,
        cancelAnnotation,
        popAnnotationFromStack,
        confirmAnnotation,
      ])
    ),
    map(() => state$.value),
    pluck('router', 'location'),
    filter(({ pathname }) => pathname.includes('/document/')),
    map(({ pathname, search }) =>
      constructDocumentUrl({
        pathname,
        query: { ...parse(search), datapointPath: undefined },
      })
    ),
    map(path => replace(path))
  )
);

const refetchAnnotationEmailsEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf([refetchAnnotationEmails])),
    switchMap(({ payload: { annotationId } }) =>
      from(api.request(endpoints.annotations.get(annotationId))).pipe(
        map(annotation => refetchAnnotationEmailsFulfilled(annotation))
      )
    )
  )
);

const fetchProcessingDurationEpic = makeEpic((action$, _, { authGetJSON$ }) =>
  action$.pipe(
    filter(isActionOf(fetchProcessingDuration)),
    switchMap(({ payload: annotationUrl }) =>
      authGetJSON$<
        ProcessingDuration & {
          annotation: Url;
        }
      >(`${annotationUrl}/processing_duration`).pipe(
        retryExponentialBackoff(),
        takeUntil(
          action$.pipe(filter(isActionOf([...validationEndingActionCreators])))
        ),
        map(fetchProcessingDurationFulfilled),
        catchError(errorHandler)
      )
    )
  )
);

const setupTimeSpentOnWindowCloseEpic = makeEpic(action$ =>
  action$.pipe(
    filter(
      isActionOf([startAnnotationFulfilled, editModeForParentStartedAction])
    ),
    switchMap(({ meta: { url } }) =>
      fromEvent<PageTransitionEvent>(window, 'pagehide', {
        capture: true,
      }).pipe(
        filter(e => !e.persisted),
        // unsubscribe from this listener when annotating is finished
        takeUntil(
          action$.pipe(filter(isActionOf(validationEndingActionCreators)))
        ),
        tap(() => {
          const processingDuration = timeSpent.stopAnnotation(
            getIDFromUrl(url)
          );

          // send a `keepalive` request and Jesus take the wheel (we don't know how it ends up)
          // sendBeacon will not let us set custom headers incl. Authorization :shrug:
          fetch(`${url}/cancel`, {
            method: 'POST',
            headers: createAuthJSONHeaders(),
            keepalive: true,
            body: JSON.stringify(convertKeys(camelToSnake)(processingDuration)),
          });
        }),
        mapTo({ type: 'CANCEL_AFTER_PAGEHIDE' })
      )
    )
  )
);

// When a tab becomes hidden, update last DP time spent
const setupTimeSpentOnTabHideEpic = makeEpic((action$, _, { authPatch$ }) =>
  action$.pipe(
    filter(isActionOf(startAnnotationFulfilled)),
    switchMap(() =>
      fromEvent<Event>(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'hidden'),
        withLatestFrom(
          action$.pipe(filter(isActionOf(lastDatapointsForTimeSpentUpdate)))
        ),
        takeUntil(
          action$.pipe(filter(isActionOf(validationEndingActionCreators)))
        ),
        filter(
          ([
            _e,
            {
              payload: { datapoints },
            },
          ]) => datapoints.length > 0
        ),
        mergeMap(
          ([
            _e,
            {
              payload: { annotationUrl, datapoints },
            },
          ]) =>
            forkJoin(
              datapoints.map(d => {
                const timeSpentUpdate = timeSpent.getElapsedDatapoint(d.id);

                // We need to return some value, so that
                // forkJoin will emit a value at the end.
                if (!timeSpentUpdate) return of(undefined);

                return authPatch$(
                  `${annotationUrl}/content/${d.id}?fields!=children`,
                  timeSpentUpdate
                ).pipe(
                  catchError((err: AjaxError) => {
                    // Ignore errors so that we can call validation ending action
                    report(err);
                    return of(undefined);
                  })
                );
              })
            ).pipe(mapTo({ type: 'UPDATED_ON_TAB_HIDE' }))
        )
      )
    )
  )
);

const startAnnotationTimespentEpic = makeEpic((action$, state$) => {
  // This is so we have access to last emitted value in case of race condition (with editModeForParentStartedAction)
  // This fires _immediately_ but the filter below ignores wrong emits
  const processingDuration$ = action$.pipe(
    filter(isActionOf(fetchProcessingDurationFulfilled)),
    shareReplay(1)
  );

  return action$.pipe(
    filter(
      isActionOf([startAnnotationFulfilled, editModeForParentStartedAction])
    ),
    tap(() => timeSpent.startAnnotationOpening()),
    switchMap(action => {
      const {
        meta: { url },
      } = action;

      // When opening edit mode for parent, two things happen:
      // annotation opening time will be 0 (unfortunate)
      // race condition between `editModeForParentStartedAction` and `addSchemasToDatapoints` would
      // prevent the stream from firing. Subscribing to state in that case is enough to trigger this epic
      const schemasLoaded$ = isActionOf(editModeForParentStartedAction)(action)
        ? state$.pipe(
            map(s => s.datapoints.content),
            filter(
              content => content.length > 0 && content.some(dp => !!dp.schema)
            ),
            take(1),
            mapTo(true)
          )
        : action$.pipe(
            filter(isActionOf(addSchemasToDatapoints)),
            take(1),
            mapTo(true)
          );

      return combineLatest([
        schemasLoaded$.pipe(
          map(() => {
            const result = timeSpent.stopAnnotationOpening();

            timeSpent.startAnnotation(getIDFromUrl(url));

            return result ?? { timeSpentOpening: 0 };
          })
        ),
        // We already have the processing duration response,
        // or we need to wait for it.
        // the stream emits immediately but potentially with an old value, hence the filter
        processingDuration$.pipe(filter(a => a.payload.annotation === url)),
      ]).pipe(
        take(1),
        tap(([{ timeSpentOpening }, { payload }]) => {
          // console.log('setting initial processing');
          // Store initial processing duration in the timer,
          // so that we have access to it even if the annotation state
          // is already cleared (e.g. when cancelAnnotation action is dispatched)
          // If the API will accept increments, this won't be necessary.

          timeSpent.setAnnotationInitialProcessingDuration(getIDFromUrl(url), {
            ...payload,
            // To be able to compare opening times of different annotations,
            // we measure only the time when the annotation is opened for the first name.
            // Opening it again, might have different performance characteristics
            // (e.g. caching, some processing going on for the first opening, ...),
            timeSpentOpening: payload.timeSpentOpening || timeSpentOpening,
          });
        }),
        mapTo({ type: 'INITIAL_PROCESSING_DURATION_SET', meta: { url } })
      );
    })
  );
});

const confirmAnnotationEpic = makeEpic(
  (action$, _state$, { authPatch$, authPost$ }) =>
    action$.pipe(
      filter(isActionOf(confirmAnnotation)),
      map(({ meta }) => meta),
      tap(({ annotationUrl }) => {
        const { durationLatestValidateUpdatedBeforeConfirm } =
          timeSpent.getValidationDurations({
            timerToDestroy: 'validation-updated',
          });

        if (durationLatestValidateUpdatedBeforeConfirm) {
          authPatch$(`${annotationUrl}/processing_duration`, {
            durationLatestValidateUpdatedBeforeConfirm,
          }).subscribe();
        }
      }),
      withProcessingDuration(
        ({ annotationUrl }) => annotationUrl,
        action$,
        authPatch$
      ),
      mergeMap(([{ annotationUrl, skipWorkflows }, processingDuration]) =>
        authPost$(`${annotationUrl}/confirm`, {
          ...processingDuration,
          skipWorkflows,
        }).pipe(
          map(() => confirmAnnotationFulfilled(annotationUrl)),
          catchError(confirmErrorHandler(annotationUrl))
        )
      )
    )
);

const navigateAfterChangeAnnotationStatusEpic = makeEpic((action$, state$) =>
  merge(
    action$.pipe(
      filter(
        isActionOf([
          confirmAnnotationFulfilled,
          deleteAnnotationFulfilled,
          postponeAnnotationFulfilled,
          rejectAnnotationFulfilled,
        ])
      )
    )
  ).pipe(
    filter(negate(isEmbedded)),
    filter(({ meta }) => !('skipRedirect' in meta) || !meta.skipRedirect),
    map(({ meta: { url } }) => {
      const documentListQuery = localStorage.getItem(DOCUMENTS_QUERY);
      const documentListUrl = documentListQuery
        ? `/documents?${documentListQuery}`
        : '/documents';

      const onConfirmAction = onConfirmNavigateSelector(state$.value);

      switch (onConfirmAction) {
        case 'sidebarNavigation.next':
          return nextAnnotableAnnotation();
        case 'sidebarNavigation.stay':
          return displayAnnotation({ url });
        case 'sidebarNavigation.leave':
          return replace(documentListUrl);
        default:
          return assertNever(onConfirmAction);
      }
    })
  )
);

const updateAnnotationAfterChangeStatusEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(
      isActionOf([postponeAnnotationFulfilled, rejectAnnotationFulfilled])
    ),
    filter(({ meta }) => meta.skipRedirect),
    // Skip reloading for actions fired from dashboard (annotation list)
    // TODO: rewrite skipRedirect flag to the three different states instead of boolean
    // (e.g. afterAction: 'skipRedirect' | 'updateAnnotation' | 'goToNextAnnotation')
    filter(() => state$.value.router.location.pathname.includes('/document/')),
    filter(negate(isEmbedded)),
    map(({ meta: { url } }) => displayAnnotation({ url }))
  )
);

const statusesWithPeriodicalCheck: Annotation['status'][] = [
  'exporting',
  'reviewing',
  'importing',
];

/**
 * This epic handles following scenarios:
 *
 * 1. As a viewer, when I open an annotation I want to see reviewer's updates.
 * 2. As an annotator, when I open an annotation that is reviewed by somebody else, I want to see their updates
 *    or when they stop reviewing, I want to start reviewing the annotation myself.
 * 3. When the annotation is still importing, I'm waiting for the data to be available.
 */
const periodicalAnnotationCheck = makeEpic((action$, state$) =>
  action$.pipe(
    filter(
      isActionOf([displayAnnotation, ...readOnlyModeEndingActionCreators])
    ),
    switchMap(action =>
      isActionOf(displayAnnotation)(action)
        ? action$.pipe(
            filter(isActionOf(fetchAnnotationFulfilled)),
            filter(
              ({
                payload: {
                  annotation: { status },
                },
              }) => statusesWithPeriodicalCheck.includes(status)
            ),
            switchMap(({ payload: { annotation } }) =>
              (annotation.status === 'importing'
                ? of({ type: 'EMPTY_ACTION' })
                : action$.pipe(filter(isActionOf(fetchDatapointsFulfilled)))
              ).pipe(
                delay(FETCH_INTERVAL),
                map(() => {
                  const hasBeenImported =
                    pagesSelector(state$.value).length > 0;

                  return isUserViewer(state$.value)
                    ? displayAnnotation({
                        id: annotation.id,
                        mode: hasBeenImported ? 'refresh' : 'full-load',
                      })
                    : startAnnotation(
                        annotation.id,
                        hasBeenImported ? 'refresh' : 'full-load'
                      );
                })
              )
            )
          )
        : EMPTY
    )
  )
);

const postponeAnnotationEpic = makeEpic(
  (action$, _state$, { authPost$, authPatch$ }) =>
    action$.pipe(
      filter(isActionOf(postponeAnnotation)),
      map(({ meta }) => meta),
      distinctUntilChanged<PostponeAnnotationMeta>(equal),
      withProcessingDuration(({ url }) => url, action$, authPatch$),
      mergeMap(([{ url, skipRedirect, onSuccess }, processingDuration]) => {
        return authPost$(`${url}/postpone`, processingDuration).pipe(
          tap(() => onSuccess?.()),
          map(() => postponeAnnotationFulfilled(url, skipRedirect, false)),
          catchError(errorHandler)
        );
      })
    )
);

const deleteAnnotationEpic = makeEpic(
  (action$, _state$, { authPost$, authPatch$ }) =>
    action$.pipe(
      filter(isActionOf(deleteAnnotation)),
      map(({ meta }) => meta),
      distinctUntilChanged<DeleteAnnotationMeta>(equal),
      withProcessingDuration(({ url }) => url, action$, authPatch$),
      mergeMap(([{ url, skipRedirect, emailId }, processingDuration]) => {
        return authPost$(`${url}/delete`, processingDuration).pipe(
          map(() =>
            deleteAnnotationFulfilled(url, skipRedirect, emailId, false)
          ),
          catchError(errorHandler)
        );
      })
    )
);

const cancelAnnotationEpic = makeEpic((action$, _, { authPost$, authPatch$ }) =>
  action$.pipe(
    filter(isActionOf([cancelAnnotation, openOriginalDocumentFromEditMode])),
    withProcessingDuration(action => action.payload.url, action$, authPatch$),
    mergeMap(
      ([
        {
          payload: { url, redirectTo },
        },
        processingDuration,
      ]) => {
        return authPost$(`${url}/cancel`, processingDuration).pipe(
          retryExponentialBackoff(),
          switchMap(() =>
            from(
              R.filter(
                [
                  cancelAnnotationFulfilled(),
                  redirectTo ? push(redirectTo) : null,
                ],
                R.isTruthy
              )
            )
          ),
          catchError(error => {
            return error.status === 409
              ? // when attempting to cancel split annotation (eg leaving parent edit mode)
                // we need to update time spent, this is the most sane way believe it or not
                error.response.code === 'conflict_status' &&
                error.response.annotation_status === 'split' &&
                processingDuration
                ? authPatch$(
                    `${url}/processing_duration`,
                    processingDuration.processingDuration
                  ).pipe(
                    mapTo(
                      redirectTo ? push(redirectTo) : { type: 'EMPTY_ACTION' }
                    )
                  )
                : of(redirectTo ? push(redirectTo) : { type: 'EMPTY_ACTION' })
              : throwError(error);
          }),
          catchError(errorHandler)
        );
      }
    )
  )
);

export const checkAnnotationStatus = makeEpic(
  (action$, state$, { authGetJSON$ }) =>
    action$.pipe(
      filter(isActionOf(startAnnotationFulfilled)),
      pluck('meta', 'url'),
      switchMap(url =>
        timer(300000, 300000).pipe(
          takeUntil(
            action$.pipe(
              filter(
                isActionOf([
                  confirmAnnotation,
                  cancelAnnotation,
                  deleteAnnotation,
                  postponeAnnotation,
                  confirmEditModeFulfilled,
                  annotationExpired,
                ])
              )
            )
          ),
          // MAT-1958: the poll is scoped to the legacy validation route. On the
          // new split screen (`/split/:id`) a successful confirm leaves the
          // annotation in status `split`, which is not `reviewing` and would be
          // misreported as expired. Filtering (rather than completing) keeps
          // the poll alive, so it resumes when the user navigates back to
          // `/document/:id`.
          filter(() =>
            isValidationPathname(state$.value.router.location.pathname)
          ),
          mergeMap(() => authGetJSON$<Annotation>(url)),
          filter(({ status }) => status !== 'reviewing'),
          map(({ id }) => annotationExpired(id)),
          catchError(errorHandler)
        )
      )
    )
);

const startValidationEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(startValidation)),
    pluck('meta', 'url'),
    filter(R.isTruthy),
    map(url => push(url))
  )
);

const handleStart =
  (
    action$: ActionsObservable<RootActionType>,
    state$: StateObservable<State>
  ) =>
  (id: number): Observable<RootActionType> =>
    userRoleNameSelector(state$.value)
      ? isUserViewer(state$.value)
        ? of(displayAnnotation({ id }))
        : of(startAnnotation(id))
      : zip(
          state$.pipe(
            pluck('user', 'id'),
            filter(_id => _id > -1)
          ),
          state$.pipe(pluck('groups', 'length'), filter(R.isTruthy)),
          state$.pipe(
            pluck('organizationGroup', 'current'),
            filter(R.isTruthy),
            filter(() => embeddedFeatureSelector(state$.value))
          )
        ).pipe(
          takeUntil(
            action$.pipe(filter(isActionOf([leaveValidation, nextAnnotation])))
          ),
          first(),
          mergeMap(() => handleStart(action$, state$)(id))
        );

export const startAnnotationFromUrl = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(locationChange)),
    pluck('payload', 'location', 'pathname'),
    // MAT-1958: the id is only tracked while we are on the legacy validation
    // route. `/document/5` -> `/split/5` -> back would otherwise be a `5, 5, 5`
    // sequence and `distinctUntilChanged` would swallow the return trip, even
    // though leaving the validation screen wiped the annotation state — the
    // screen would then be stuck loading forever.
    map(pathname =>
      isValidationPathname(pathname) ? getCurrentAnnotationId(pathname) : null
    ),
    distinctUntilChanged(),
    filter(() => !state$.value.annotation.id),
    filter(R.isTruthy),
    mergeMap(handleStart(action$, state$))
  )
);

// MAT-1958: the new split route (`/split/:id`, embedded or not) is an edit mode
// screen just like the legacy `/document/:id/edit` nested route.
const isEditModePathname = (pathname: string): boolean =>
  pathname.endsWith('/edit') || isSplitPathname(pathname);

// Redirecting between different annotations, doesn't properly start the new annotation.
const startAnnotationModeRedirectEpic = makeEpic((_, state$) =>
  state$.pipe(
    map(s => s.router?.location?.pathname),
    filter(R.isTruthy),
    distinctUntilChanged(),
    pairwise(),
    filter(([prevPathname, nextPathname]) => {
      const prevAnnotationId = getCurrentAnnotationId(prevPathname);
      const nextAnnotationId = getCurrentAnnotationId(nextPathname);

      const prevIsEditMode = isEditModePathname(prevPathname);
      const nextIsEditMode = isEditModePathname(nextPathname);

      const routerAction = state$.value.router.action;

      return (
        !!prevAnnotationId &&
        !!nextAnnotationId &&
        prevAnnotationId !== nextAnnotationId &&
        (prevIsEditMode || nextIsEditMode || routerAction === 'POP')
      );
    }),
    mergeMap(() => {
      return concat(
        // clear local state and cancel in-flight requests for the old annotation
        // nextAnnotation also call handleStart via resetDatapointPath epic which is not correct but it works
        of(nextAnnotation('cancel-previous-annotation'))
      );
    })
  )
);

const checkSuggestedSplitAvailabilityEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchAnnotationFulfilled)),
    map(
      ({
        payload: {
          annotation: { suggestedEdit },
        },
      }) => suggestedEdit
    ),
    filter(R.isTruthy),
    filter(() => !state$.value.ui.readOnly),
    // We know that it comes as string from backend, will be replaced later
    map(url => fetchSuggestedEdit(url as string))
  )
);

const fetchSuggestedSplitEpic = makeEpic((action$, _, { authGetJSON$ }) =>
  action$.pipe(
    filter(isActionOf(fetchSuggestedEdit)),
    pluck('payload'),
    switchMap(url =>
      authGetJSON$<SuggestedEdit>(url, {
        excludeKeysFromConversion: ['values'],
      }).pipe(map(fetchSuggestedEditFulfilled), catchError(errorHandler))
    )
  )
);

const rejectAnnotationEpic = makeEpic(
  (action$, _state$, { authPost$, authPatch$ }) =>
    action$.pipe(
      filter(isActionOf(rejectAnnotation)),
      withProcessingDuration(({ meta: { url } }) => url, action$, authPatch$),
      mergeMap(
        ([
          {
            meta: { url, skipRedirect, onSuccess },
          },
          processingDuration,
        ]) => {
          return authPost$(`${url}/reject`, processingDuration).pipe(
            tap(() => onSuccess?.()),
            map(() => rejectAnnotationFulfilled(url, skipRedirect, false)),
            catchError(errorHandler)
          );
        }
      )
    )
);

const updateProcessingDurationAfterExpiredEpic = makeEpic(
  (action$, _state$, { authPatch$ }) =>
    action$.pipe(
      filter(isActionOf(annotationExpired)),
      withProcessingDuration(({ payload }) => payload, action$, authPatch$),
      mergeMap(([{ payload }, processingDuration]) => {
        if (!processingDuration) {
          return EMPTY;
        }

        return authPatch$(
          `${apiUrl}/annotations/${payload}/processing_duration`,
          processingDuration.processingDuration
        ).pipe(mapTo({ type: 'EMPTY_ACTION' }), catchError(errorHandler));
      })
    )
);

const TARGET_PREVALIDATION_COUNT = 1;

const prevalidatedAnnotationUrls = new Set<string>();

const prevalidateAnnotation$ = (
  annotationUrl: string,
  authPost$: <T = unknown>(url: string, body?: unknown) => Observable<T>,
  state$: StateObservable<State>
): Observable<{ type: string }> => {
  prevalidatedAnnotationUrls.add(annotationUrl);

  const validate$ = authPost$(`${annotationUrl}/content/validate`, {
    updatedDatapointIds: [],
    actions: ['user_update', 'started'],
  }).pipe(catchError(() => EMPTY));

  const cancel$ = of(null).pipe(
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const currentAnnotationId = getCurrentAnnotationId(
        state.router.location.pathname
      );
      const prevalidatedAnnotationId = getIDFromUrl(annotationUrl);

      if (currentAnnotationId === prevalidatedAnnotationId) {
        prevalidatedAnnotationUrls.delete(annotationUrl);
        return EMPTY;
      }

      return authPost$(`${annotationUrl}/cancel?prevalidate=true`).pipe(
        tap(() => prevalidatedAnnotationUrls.delete(annotationUrl)),
        catchError(() => {
          prevalidatedAnnotationUrls.delete(annotationUrl);
          return EMPTY;
        })
      );
    })
  );

  return concat(validate$, cancel$).pipe(
    mapTo({ type: 'EMPTY_ACTION' }),
    catchError(() => of({ type: 'EMPTY_ACTION' }))
  );
};

type PrevalidationAccumulator = {
  readonly remainingStack: readonly number[];
  readonly startedAnnotations: readonly string[];
};

const createNextState = (
  state: PrevalidationAccumulator,
  annotationUrl: Url | null
): PrevalidationAccumulator => {
  if (!annotationUrl) {
    return { remainingStack: [], startedAnnotations: state.startedAnnotations };
  }

  const annotationId = getIDFromUrl(annotationUrl);

  return {
    remainingStack: state.remainingStack.filter(id => id !== annotationId),
    startedAnnotations: [...state.startedAnnotations, annotationUrl],
  };
};

const stopState = (
  state: PrevalidationAccumulator
): PrevalidationAccumulator => ({
  remainingStack: [],
  startedAnnotations: state.startedAnnotations,
});

const shouldContinue = (state: PrevalidationAccumulator, targetCount: number) =>
  state.startedAnnotations.length < targetCount &&
  state.remainingStack.length > 0;

/**
 * Sequentially calls /next to start annotations until we have `count` started.
 *
 * How it works:
 * 1. Start with initial state: full stack, no started annotations
 * 2. `expand` recursively emits new states until EMPTY is returned
 * 3. Each iteration:
 *    - Calls /next with remaining stack → backend returns next to_review annotation (already started)
 *    - Removes that annotation from remaining stack
 *    - Adds its URL to startedAnnotations
 * 4. Stops when: we have enough annotations OR stack is empty OR /next returns null OR error
 * 5. `reduce` collects the final startedAnnotations array
 */
const startNextAnnotations$ = (
  initialStack: readonly number[],
  count: number,
  statuses: AnnotationStatus[],
  authPost$: <T = unknown>(url: string, body?: unknown) => Observable<T>
): Observable<string[]> => {
  const initialState: PrevalidationAccumulator = {
    remainingStack: initialStack,
    startedAnnotations: [],
  };

  return of(initialState).pipe(
    expand((state): Observable<PrevalidationAccumulator> => {
      if (!shouldContinue(state, count)) {
        return EMPTY;
      }

      return authPost$<{ annotation: Url }>(`${apiUrl}/annotations/next`, {
        annotations: state.remainingStack.map(
          id => `${absoluteApiUrl}/annotations/${id}`
        ),
        ...(statuses.length > 0 && { statuses }),
      }).pipe(
        map(({ annotation }) => createNextState(state, annotation)),
        catchError(() => of(stopState(state)))
      );
    }),
    reduce<PrevalidationAccumulator, string[]>(
      (_, state) => [...state.startedAnnotations],
      []
    )
  );
};

const prevalidateNextAnnotationsEpic = makeEpic(
  (action$, state$, { authPost$ }) =>
    action$.pipe(
      filter(isActionOf(startAnnotationFulfilled)),
      filter(() => prevalidateAnnotationsEnabledSelector(state$.value)),
      mergeMap(({ meta: { url: currentAnnotationUrl } }) => {
        const currentAnnotationId = getIDFromUrl(currentAnnotationUrl);
        const { stack } = state$.value;
        const statuses = getReviewableStatusesFromAnnotationsQuery();

        const currentIndex = stack.indexOf(currentAnnotationId);

        const remainingStack = R.pipe(stack, R.drop(currentIndex + 1));

        if (remainingStack.length === 0) {
          return of({ type: 'EMPTY_ACTION' });
        }

        return startNextAnnotations$(
          remainingStack,
          TARGET_PREVALIDATION_COUNT,
          statuses,
          authPost$
        ).pipe(
          mergeMap(annotationUrls =>
            from(annotationUrls).pipe(
              mergeMap(url => prevalidateAnnotation$(url, authPost$, state$))
            )
          ),
          catchError(() => EMPTY)
        );
      }),
      catchError(() => of({ type: 'EMPTY_ACTION' }))
    )
);

const cancelPrevalidatedOnWindowCloseEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(startAnnotationFulfilled)),
    filter(() => prevalidateAnnotationsEnabledSelector(state$.value)),
    switchMap(() =>
      fromEvent<PageTransitionEvent>(window, 'pagehide', {
        capture: true,
      }).pipe(
        filter(e => !e.persisted),
        tap(() => {
          prevalidatedAnnotationUrls.forEach(url => {
            fetch(`${url}/cancel?prevalidate=true`, {
              method: 'POST',
              headers: createAuthJSONHeaders(),
              keepalive: true,
            });
          });
          prevalidatedAnnotationUrls.clear();
        }),
        mapTo({ type: 'CANCEL_PREVALIDATED_AFTER_PAGEHIDE' })
      )
    )
  )
);

export default combineEpics(
  cancelAnnotationEpic,
  checkAnnotationStatus,
  confirmAnnotationEpic,
  deleteAnnotationEpic,
  fetchAnnotationEpic,
  fetchAnnotationDataEpic,
  resetDatapointPath,
  navigateAfterChangeAnnotationStatusEpic,
  nextAnnotableAnnotationEpic,
  nextAnnotationEpic,
  periodicalAnnotationCheck,
  postponeAnnotationEpic,
  startAnnotationEpic,
  startValidationEpic,
  startAnnotationTimespentEpic,
  setupTimeSpentOnWindowCloseEpic,
  setupTimeSpentOnTabHideEpic,
  redirectAfterFetchAnnotation,
  startAnnotationFromUrl,
  startAnnotationModeRedirectEpic,
  fetchProcessingDurationEpic,
  checkSuggestedSplitAvailabilityEpic,
  fetchSuggestedSplitEpic,
  rejectAnnotationEpic,
  updateAnnotationAfterChangeStatusEpic,
  updateProcessingDurationAfterExpiredEpic,
  refetchAnnotationEmailsEpic,
  prevalidateNextAnnotationsEpic,
  cancelPrevalidatedOnWindowCloseEpic
);
